import { Component, OnInit, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { 
  Return, 
  ReturnStatus, 
  ReturnFilters 
} from '../../../models/return.models';
import { ReturnsService } from '../../../services/returns.service';
import { CajaService } from '../../../services/caja.service';
import { GeneralesService } from '../../../services/generales.service';
import { ParametrosService } from '../../../services/parametros.service';
import { CashBoxSession } from '../../../models/cashBoxSession.models';
import { Caja } from '../../../models/caja.models';
import { environment } from '../../../../environments/environment';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-procesar-devolucion',
  templateUrl: './procesar-devolucion.component.html',
  styleUrls: ['./procesar-devolucion.component.css']
})
export class ProcesarDevolucionComponent implements OnInit {

  returns: Return[] = [];
  filteredReturns: Return[] = [];
  isLoading = false;
  selectedReturn: Return | null = null;
  
  // Gestión de caja
  cajaActual: Caja | null = null;
  cashBoxSession: CashBoxSession | null = null;
  saldoDisponible = 0;
  
  // Medios de pago
  mediosPago: any[] = [];
  selectedPaymentMethod: any = null;
  
  // Filtros y búsqueda
  searchTerm = '';
  statusFilter: ReturnStatus | null = null;
  dateFromFilter: string = '';
  dateToFilter: string = '';
  
  // Paginación
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  
  // Debug mode
  debugMode = false;

  // Traducciones para motivos
  reasonLabels = {
    'DEFECTIVE': 'Producto defectuoso',
    'WRONG_ITEM': 'Producto incorrecto',
    'NOT_AS_DESCRIBED': 'No coincide con descripción',
    'CUSTOMER_CHANGED_MIND': 'Cliente cambió de opinión',
    'DAMAGED_IN_SHIPPING': 'Dañado en envío',
    'OTHER': 'Otros'
  };

  constructor(
    private dialogRef: MatDialogRef<ProcesarDevolucionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private returnsService: ReturnsService,
    private cajaService: CajaService,
    private generalesService: GeneralesService,
    private parametrosService: ParametrosService,
    private router: Router
  ) { }

  ngOnInit(): void {
    // Activar debug mode en desarrollo
    this.debugMode = !environment.production;
    this.initializeComponent();
  }

  /**
   * Inicializa el componente verificando caja abierta
   */
  private async initializeComponent(): Promise<void> {
    this.isLoading = true;
    
    try {
      // Verificar si hay caja abierta
      await this.verificarCajaAbierta();
      
      // Cargar medios de pago
      await this.loadPaymentMethods();
      
      // Cargar devoluciones
      await this.loadApprovedReturns();
      
      // Obtener saldo disponible
      if (this.cashBoxSession) {
        await this.obtenerSaldoDisponible();
      }
      
    } catch (error) {
      console.error('Error initializing component:', error);
      Swal.fire('Error', 'Error al inicializar el componente', 'error');
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Verifica si hay una caja abierta
   */
  private verificarCajaAbierta(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.generalesService.getCajaAbierta().subscribe({
        next: (data) => {
          if (data) {
            this.cajaActual = data.cashBox as Caja;
            this.cashBoxSession = data as CashBoxSession;
            resolve();
          } else {
            Swal.fire({
              title: 'Caja Cerrada',
              text: 'No hay una caja abierta. Debe abrir la caja para procesar devoluciones.',
              icon: 'warning',
              confirmButtonText: 'Aceptar'
            }).then(() => {
              this.dialogRef.close({ needsOpenCash: true });
            });
            reject(new Error('No open cash session'));
          }
        },
        error: (error) => {
          console.error('Error checking cash session:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Carga los medios de pago disponibles
   */
  private loadPaymentMethods(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.parametrosService.getMediosPagos().subscribe({
        next: (data) => {
          this.mediosPago = data || [];
          // Seleccionar efectivo por defecto
          const efectivo = this.mediosPago.find(mp => 
            mp.name?.toLowerCase().includes('efectivo')
          );
          if (efectivo) {
            this.selectedPaymentMethod = efectivo;
          }
          resolve();
        },
        error: (error) => {
          console.error('Error loading payment methods:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Obtiene el saldo disponible en caja
   */
  private obtenerSaldoDisponible(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.cashBoxSession?.id) {
        reject(new Error('No cash session ID'));
        return;
      }

      this.generalesService.getSumaMovientosByCajaSesion(this.cashBoxSession.id).subscribe({
        next: (data) => {
          this.saldoDisponible = data?.balance?.totalCash || 0;
          resolve();
        },
        error: (error) => {
          console.error('Error getting available balance:', error);
          this.saldoDisponible = 0;
          resolve(); // No fallar por esto
        }
      });
    });
  }

  /**
   * Carga las devoluciones aprobadas listas para procesar
   */
  loadApprovedReturns(): Promise<void> {
    return new Promise((resolve, reject) => {
      const filters: ReturnFilters = {
        status: ReturnStatus.APPROVED,
        page: this.currentPage,
        limit: this.pageSize
      };

      // Agregar filtros adicionales si están definidos
      if (this.dateFromFilter) {
        filters.dateFrom = this.dateFromFilter;
      }
      if (this.dateToFilter) {
        filters.dateTo = this.dateToFilter;
      }

      this.returnsService.getReturns(filters).subscribe({
        next: (response) => {
          console.log('=== RAW RESPONSE ===');
          console.log('Full response:', response);
          
          this.returns = response.data || response;
          this.totalItems = response.total || this.returns.length;
          
          // Debug: revisar datos de clientes solo en modo desarrollo
          console.log('=== PROCESSED RETURNS ===');
          console.log('Returns array:', this.returns);
          console.log('Returns length:', this.returns.length);
          
          if (this.returns.length > 0) {
            console.log('=== FIRST RETURN ANALYSIS ===');
            const firstReturn = this.returns[0];
            console.log('First return:', firstReturn);
            console.log('First return keys:', Object.keys(firstReturn));
            console.log('First return client:', firstReturn.client);
            console.log('First return client type:', typeof firstReturn.client);
            
            if (firstReturn.client) {
              console.log('First return client keys:', Object.keys(firstReturn.client));
              console.log('Client structure:', JSON.stringify(firstReturn.client, null, 2));
            }
            
            // Probar el método getClientName con el primer cliente
            console.log('=== TESTING getClientName ===');
            const testResult = this.getClientName(firstReturn.client);
            console.log('getClientName result:', testResult);
          }
          
          this.applyFilters();
          resolve();
        },
        error: (error) => {
          console.error('Error loading approved returns:', error);
          Swal.fire('Error', 'Error al cargar las devoluciones aprobadas', 'error');
          reject(error);
        }
      });
    });
  }

  /**
   * Aplica filtros locales a las devoluciones
   */
  applyFilters(): void {
    this.filteredReturns = this.returns.filter(returnItem => {
      const searchTerm = this.searchTerm?.toLowerCase() || '';
      
      if (!searchTerm) return true;
      
      const matchesReturnNumber = returnItem.returnNumber?.toLowerCase().includes(searchTerm);
      const matchesCompanyName = returnItem.client?.companyName?.toLowerCase().includes(searchTerm);
      const matchesDocumentNumber = returnItem.client?.documentNumber?.toLowerCase().includes(searchTerm);
      const matchesEmail = returnItem.client?.email?.toLowerCase().includes(searchTerm);
      
      return matchesReturnNumber || matchesCompanyName || matchesDocumentNumber || matchesEmail;
    });
  }

  /**
   * Búsqueda en tiempo real
   */
  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.applyFilters();
  }

  /**
   * Cambio de filtro de fechas
   */
  onDateFilterChange(): void {
    this.currentPage = 1;
    this.loadApprovedReturns();
  }

  /**
   * Procesa la devolución seleccionada
   */
  processSelectedReturn(): void {
    if (!this.selectedReturn) {
      Swal.fire('Atención', 'Debe seleccionar una devolución para procesar', 'warning');
      return;
    }

    if (!this.selectedPaymentMethod) {
      Swal.fire('Atención', 'Debe seleccionar un medio de pago para el reembolso', 'warning');
      return;
    }

    const totalAmount = this.calculateTotal(this.selectedReturn);
    
    // Validar saldo disponible solo para efectivo
    if (this.selectedPaymentMethod.name?.toLowerCase().includes('efectivo') && 
        totalAmount > this.saldoDisponible) {
      Swal.fire({
        title: 'Saldo Insuficiente',
        text: `El monto del reembolso ($${totalAmount.toFixed(2)}) es superior al saldo disponible en caja ($${this.saldoDisponible.toFixed(2)}).`,
        icon: 'warning',
        confirmButtonText: 'Aceptar'
      });
      return;
    }

    Swal.fire({
      title: 'Procesar Devolución',
      html: `
        <div class="text-left">
          <p><strong>Devolución:</strong> ${this.selectedReturn.returnNumber}</p>
          <p><strong>Cliente:</strong> ${this.getClientDisplayName(this.selectedReturn.client)}</p>
          <p><strong>Monto:</strong> $${totalAmount.toFixed(2)}</p>
          <p><strong>Medio de pago:</strong> ${this.selectedPaymentMethod.name}</p>
          <br>
          <p>¿Está seguro que desea procesar esta devolución? Se realizará el reembolso y se actualizará el inventario.</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Procesar',
      cancelButtonText: 'Cancelar',
      customClass: {
        htmlContainer: 'text-left'
      }
    }).then((result) => {
      if (result.isConfirmed && this.selectedReturn) {
        this.executeReturnProcess();
      }
    });
  }

  /**
   * Ejecuta el proceso de devolución
   */
  private executeReturnProcess(): void {
    if (!this.selectedReturn || !this.cashBoxSession) return;
    
    this.isLoading = true;
    const totalAmount = this.calculateTotal(this.selectedReturn);
    
    // Primero procesar la devolución
    this.returnsService.processReturn(this.selectedReturn.id).subscribe({
      next: () => {
        // Luego crear la operación de caja para el egreso
        this.createCashOperation(totalAmount);
      },
      error: (error) => {
        console.error('Error processing return:', error);
        Swal.fire('Error', 'Error al procesar la devolución', 'error');
        this.isLoading = false;
      }
    });
  }

  /**
   * Crea la operación de caja para el egreso del reembolso
   */
  private createCashOperation(amount: number): void {
    if (!this.selectedReturn || !this.cashBoxSession || !this.selectedPaymentMethod) return;

    const today = new Date();
    const formattedDate = `${('0' + today.getDate()).slice(-2)}/${('0' + (today.getMonth() + 1)).slice(-2)}/${today.getFullYear()}`;
    
    const cashOperationDto = {
      amount: Number(amount),
      cashOperationTypeId: 7, // ID para operaciones de egreso/reembolso
      description: `Reembolso por devolución ${this.selectedReturn.returnNumber}`,
      type: 'egreso',
      cashSessionId: Number(this.cashBoxSession.id),
      paymentMethods: [{
        paymentMethodId: Number(this.selectedPaymentMethod.id),
        amount: Number(amount)
      }],
      voucher: {
        header: {
          receiptBookId: this.cajaActual?.salesPoint?.receiptBooks?.[0]?.id || 1,
          number: 1, // Será asignado por el backend
          dateFrom: formattedDate,
          dateTo: formattedDate,
          expirationDate: formattedDate,
          receptorCuit: null,
          priceListId: 1
        },
        details: [{
          id: 1,
          code: "REEMBOLSO",
          description: `Reembolso devolución ${this.selectedReturn.returnNumber}`,
          quantity: 1,
          unitPrice: Number(amount),
          subtotal: Number(amount),
          variantId: 1
        }],
        currencyId: 52, // PESOS
        voucherTypeId: 1,
        conceptId: 1,
        clientId: this.selectedReturn.clientId
      }
    };

    this.cajaService.guardarOperacionCaja(cashOperationDto).subscribe({
      next: () => {
        this.isLoading = false;
        Swal.fire('Éxito', 'Devolución procesada exitosamente', 'success');
        
        // Cerrar el diálogo y retornar la información
        this.dialogRef.close({
          processed: true,
          return: this.selectedReturn,
          amount: amount,
          paymentMethod: this.selectedPaymentMethod
        });
      },
      error: (error) => {
        console.error('Error creating cash operation:', error);
        Swal.fire('Error', 'La devolución se procesó pero hubo un error al registrar el movimiento de caja', 'warning');
        this.isLoading = false;
        
        // Aún así cerrar con éxito parcial
        this.dialogRef.close({
          processed: true,
          return: this.selectedReturn,
          amount: amount,
          paymentMethod: this.selectedPaymentMethod,
          cashOperationError: true
        });
      }
    });
  }

  /**
   * Selecciona una devolución para procesar
   */
  selectReturn(returnItem: Return): void {
    this.selectedReturn = returnItem;
  }

  /**
   * Navega a ver detalle de la devolución
   */
  viewReturnDetail(returnItem: Return): void {
    this.dialogRef.close();
    this.router.navigate(['/devoluciones/detalle', returnItem.id]);
  }

  /**
   * Navega a crear nueva devolución
   */
  createNewReturn(): void {
    this.dialogRef.close();
    this.router.navigate(['/devoluciones/crear']);
  }

  /**
   * Calcula el total de una devolución
   */
  calculateTotal(returnItem: Return): number {
    if (!returnItem.details) return 0;
    
    return returnItem.details.reduce((total, detail) => {
      return total + (detail.totalPrice || 0);
    }, 0);
  }

  /**
   * Formatea el nombre del cliente para mostrar
   */
  getClientName(client: any): string {
    // Debug logging más detallado
    if (this.debugMode) {
      console.log('=== DEBUG CLIENT NAME ===');
      console.log('Client raw data:', client);
      console.log('Client type:', typeof client);
      console.log('Client keys:', client ? Object.keys(client) : 'null');
      
      if (client) {
        console.log('companyName:', client.companyName, typeof client.companyName);
        console.log('documentNumber:', client.documentNumber, typeof client.documentNumber);
        console.log('number:', client.number, typeof client.number);
        console.log('email:', client.email, typeof client.email);
        console.log('All client properties:', JSON.stringify(client, null, 2));
      }
    }
    
    if (!client) {
      console.log('Client is null/undefined, returning Consumidor Final');
      return 'Consumidor Final';
    }
    
    // Si tiene companyName y no está vacío
    if (client.companyName && typeof client.companyName === 'string' && client.companyName.trim() !== '') {
      const result = client.companyName.trim();
      console.log('Using companyName:', result);
      return result;
    }
    
    // Si tiene documentNumber
    if (client.documentNumber && typeof client.documentNumber === 'string' && client.documentNumber.trim() !== '') {
      const result = `Cliente: ${client.documentNumber.trim()}`;
      console.log('Using documentNumber:', result);
      return result;
    }
    
    // Si tiene número de cliente
    if (client.number && (typeof client.number === 'string' || typeof client.number === 'number')) {
      const result = `Cliente #${client.number}`;
      console.log('Using number:', result);
      return result;
    }
    
    // Si tiene email
    if (client.email && typeof client.email === 'string' && client.email.trim() !== '') {
      const result = client.email.trim();
      console.log('Using email:', result);
      return result;
    }
    
    // Si tiene id
    if (client.id && (typeof client.id === 'string' || typeof client.id === 'number')) {
      const result = `Cliente ID: ${client.id}`;
      console.log('Using id:', result);
      return result;
    }
    
    // Intentar con propiedades que podrían tener otros nombres
    const possibleNameFields = ['name', 'fullName', 'clientName', 'customerName', 'businessName'];
    for (const field of possibleNameFields) {
      if (client[field] && typeof client[field] === 'string' && client[field].trim() !== '') {
        const result = client[field].trim();
        console.log(`Using ${field}:`, result);
        return result;
      }
    }
    
    // Si llega hasta aquí, mostrar información de debug
    console.error('Client has no recognizable name fields:', client);
    console.error('Available fields:', Object.keys(client));
    
    // Fallback con información de debug
    return `Cliente (${Object.keys(client).length} props)`;
  }

  /**
   * Obtiene el nombre de display del cliente (método alternativo)
   */
  getClientDisplayName(client: any): string {
    return this.getClientName(client);
  }

  /**
   * Actualiza el medio de pago seleccionado
   */
  onPaymentMethodChange(paymentMethod: any): void {
    this.selectedPaymentMethod = paymentMethod;
  }

  /**
   * Cierra el diálogo sin procesar
   */
  cancel(): void {
    this.dialogRef.close({ processed: false });
  }

  /**
   * Obtiene la lista para mostrar (filtrada o completa)
   */
  get displayReturns(): Return[] {
    return this.filteredReturns.length > 0 ? this.filteredReturns : this.returns;
  }

  /**
   * Verifica si hay resultados después de aplicar filtros
   */
  get hasResults(): boolean {
    return this.displayReturns.length > 0;
  }

  /**
   * Verifica si se puede procesar la devolución seleccionada
   */
  get canProcessReturn(): boolean {
    return !!(this.selectedReturn && this.selectedPaymentMethod && !this.isLoading);
  }

  /**
   * Debug helper para el template
   */
  debugClient(client: any, index: number): string {
    console.log(`=== DEBUG CLIENT ${index} ===`);
    console.log('Client:', client);
    console.log('Type:', typeof client);
    console.log('Keys:', client ? Object.keys(client) : 'null');
    console.log('JSON:', JSON.stringify(client, null, 2));
    
    const result = this.getClientName(client);
    console.log('getClientName result:', result);
    
    return result;
  }

  /**
   * Método alternativo para obtener el nombre del cliente
   * Vamos a probar con una lógica diferente
   */
  getClientNameAlt(client: any): string {
    console.log('=== ALT METHOD ===');
    console.log('Client input:', client);
    
    if (!client) return 'Sin cliente';
    
    // Convertir a string y verificar
    const fields = [
      'companyName', 
      'businessName', 
      'name', 
      'fullName',
      'documentNumber', 
      'email',
      'number',
      'id'
    ];
    
    for (const field of fields) {
      const value = client[field];
      console.log(`Checking ${field}:`, value, typeof value);
      
      if (value !== null && value !== undefined) {
        const stringValue = String(value).trim();
        if (stringValue && stringValue !== 'undefined' && stringValue !== 'null') {
          console.log(`Using ${field} with value:`, stringValue);
          return field === 'documentNumber' ? `Doc: ${stringValue}` :
                 field === 'number' || field === 'id' ? `Cliente #${stringValue}` :
                 stringValue;
        }
      }
    }
    
    return 'Cliente sin datos';
  }
}